/**
 * Turning `get_onlinecode` rows into `Coupon` (MYP-73).
 *
 * The document prints three fields for this endpoint — `id`, `typename`, `statusname`. The live
 * call returns eighteen, and the three it prints are the three least worth having: two of them are
 * labels, and one of those labels is actively misleading (see `statusLabelIsNotUsed` below).
 *
 * The endpoint is `legacyStatusRow` — `{status: "1", row: [...]}` — which is the one thing the
 * document got right about it. Note that `status` here is the *result code*, while `status` on a
 * row is the coupon's own state, and `status` on `alipay_page2` is an account's KYC state. Three
 * meanings, one key name, two endpoints wired by the same ticket.
 */

import { toIsoTimestamp } from './packages-wire.js';
import type { Coupon } from './coupons.js';

type Row = Readonly<Record<string, unknown>>;

/** Which language a coupon's type label resolves to. The vendor stores Chinese and English. */
export type CouponLanguage = 'zh' | 'en';

function text(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
}

/** A decimal amount on the wire → integer cents, without float drift. */
function cents(value: unknown): number | undefined {
  const raw = text(value);
  if (raw === undefined) return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : undefined;
}

/**
 * Why `statusname` / `statusname_en` are read off the wire and then thrown away.
 *
 * Every row on the test account with `status: "N"` is labelled 无效 / "invalid" — all 72 of them,
 * regardless of dates or of `status1`. On this account that happens to be true, because every
 * coupon is expired. It is not a label that can be trusted for a coupon that is *not*: a customer
 * holding a coupon they could spend today would be told it is invalid.
 *
 * So the flag is read and the label is not. `status: "U"` means the vendor has marked it spent;
 * everything else is a coupon this app dates for itself in `deriveCouponStatus`.
 *
 * (Whether the vendor derives that label from `status` alone or also from the dates cannot be
 * settled from this account — there are no unexpired rows to tell the two apart. Deriving is the
 * safe side of that: it can only ever be too cautious, never wrong in the customer's favour.)
 */
const USED_FLAG = 'U';

/**
 * One row → one `Coupon`, or undefined for a row with no code to show.
 *
 * The code is what a customer types into an order, so a row without one has nothing to offer and
 * is dropped rather than rendered as a blank card.
 */
export function normaliseCoupon(row: unknown, language: CouponLanguage): Coupon | undefined {
  if (typeof row !== 'object' || row === null) return undefined;
  const record = row as Row;

  const code = text(record['codeno']);
  if (code === undefined) return undefined;

  const typeCode = text(record['type']) ?? 'UNKNOWN';
  const label = language === 'zh' ? text(record['typename']) : text(record['typename_en']);

  const startDate = toIsoTimestamp(record['strdate']);
  const endDate = toIsoTimestamp(record['enddate']);
  // A coupon with no window has no status this app can derive, and `deriveCouponStatus` would
  // compare against an Invalid Date — which is false either way, quietly making it look valid.
  if (startDate === undefined || endDate === undefined) return undefined;

  return {
    id: text(record['id']) ?? code,
    code,
    // The other language, then the code itself: a card headed by a bare type code is poor, but a
    // card headed by nothing is worse.
    title: label ?? text(record['typename_en']) ?? text(record['typename']) ?? typeCode,
    typeCode,
    valueCents: cents(record['fee']) ?? 0,
    // As the vendor spells it — "RM", "RMB". Normalised only where it is formatted.
    currency: text(record['currency']) ?? 'RM',
    startDate,
    endDate,
    minSpendCents: cents(record['leastcost']),
    used: text(record['status'])?.toUpperCase() === USED_FLAG,
  };
}

/** Every usable row, in the order the vendor sent them — `sortCoupons` decides the display order. */
export function normaliseCoupons(
  rows: readonly unknown[],
  language: CouponLanguage,
): readonly Coupon[] {
  const coupons: Coupon[] = [];

  for (const row of rows) {
    const parsed = normaliseCoupon(row, language);
    if (parsed) coupons.push(parsed);
  }

  return coupons;
}
